/**
 * useTaskActions Hook - Task CRUD Operations
 *
 * Custom hook that provides all task-related action methods from Zustand store.
 * This hook ONLY subscribes to actions (stable function references), which means
 * it will NEVER cause component re-renders.
 *
 * @example
 * ```tsx
 * const {
 *   createTask,
 *   updateTask,
 *   deleteTask,
 *   addRequirement,
 *   addComment
 * } = useTaskActions();
 *
 * // Use actions without causing re-renders
 * createTask({ title: 'New Task', ... });
 * updateTask(taskId, { status: TaskStatus.Completed });
 * addRequirement(taskId, { text: 'New requirement', completed: false });
 * ```
 *
 * Performance:
 * - 0 re-renders - all subscriptions are to stable function references
 * - Actions don't change between renders (Zustand guarantee)
 * - Ideal for components that only need to dispatch actions
 */

import { useGeoMarkupStore } from '../store';

/**
 * Custom hook for task CRUD operations
 * Actions don't cause re-renders (stable function references)
 */
export const useTaskActions = () => {
  // ===== TASK CRUD OPERATIONS (Local) =====
  // All function references are stable - they never change

  const createTask = useGeoMarkupStore((state) => state.tasks.createTask);
  const updateTask = useGeoMarkupStore((state) => state.tasks.updateTask);
  const deleteTask = useGeoMarkupStore((state) => state.tasks.deleteTask);
  const setTaskStatus = useGeoMarkupStore((state) => state.tasks.setTaskStatus);
  const selectTask = useGeoMarkupStore((state) => state.tasks.selectTask);

  // ===== TASK API OPERATIONS =====
  // These call the backend API and update local state on success

  const fetchTasks = useGeoMarkupStore((state) => state.tasks.fetchTasks);
  const fetchTaskById = useGeoMarkupStore((state) => state.tasks.fetchTaskById);
  const createTaskApi = useGeoMarkupStore((state) => state.tasks.createTaskApi);
  const updateTaskApi = useGeoMarkupStore((state) => state.tasks.updateTaskApi);
  const deleteTaskApi = useGeoMarkupStore((state) => state.tasks.deleteTaskApi);

  // ===== REQUIREMENT OPERATIONS =====

  const addRequirement = useGeoMarkupStore(
    (state) => state.tasks.addRequirement
  );
  const updateRequirement = useGeoMarkupStore(
    (state) => state.tasks.updateRequirement
  );
  const deleteRequirement = useGeoMarkupStore(
    (state) => state.tasks.deleteRequirement
  );

  // ===== ATTACHMENT OPERATIONS =====

  const addAttachment = useGeoMarkupStore((state) => state.tasks.addAttachment);
  const deleteAttachment = useGeoMarkupStore(
    (state) => state.tasks.deleteAttachment
  );

  // ===== COMMENT OPERATIONS =====

  const addComment = useGeoMarkupStore((state) => state.tasks.addComment);
  const receiveComment = useGeoMarkupStore(
    (state) => state.tasks.receiveComment
  );
  const updateComment = useGeoMarkupStore((state) => state.tasks.updateComment);
  const deleteComment = useGeoMarkupStore((state) => state.tasks.deleteComment);

  // ===== HISTORY OPERATIONS =====

  const addHistoryEvent = useGeoMarkupStore(
    (state) => state.tasks.addHistoryEvent
  );

  // ===== API STATE HELPERS =====

  const clearError = useGeoMarkupStore((state) => state.tasks.clearError);

  // ===== COMPUTED METHODS =====
  // These are also stable function references

  const getTaskById = useGeoMarkupStore((state) => state.tasks.getTaskById);
  const getTasksByStatus = useGeoMarkupStore(
    (state) => state.tasks.getTasksByStatus
  );
  const getCompletionPercentage = useGeoMarkupStore(
    (state) => state.tasks.getCompletionPercentage
  );
  return {
    // Task CRUD (Local - for optimistic updates)
    createTask,
    updateTask,
    deleteTask,
    setTaskStatus,
    selectTask,

    // Task API Operations (for backend sync)
    fetchTasks,
    fetchTaskById,
    createTaskApi,
    updateTaskApi,
    deleteTaskApi,

    // Requirements
    addRequirement,
    updateRequirement,
    deleteRequirement,

    // Attachments
    addAttachment,
    deleteAttachment,

    // Comments
    addComment,
    receiveComment,
    updateComment,
    deleteComment,

    // History
    addHistoryEvent,

    // API State Helpers
    clearError,

    // Computed methods
    getTaskById,
    getTasksByStatus,
    getCompletionPercentage,
  };
};
